import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatRM, formatDate, formatDateTime, STATUSES, BACKEND_URL } from "@/lib/api";
import { JobItemsList } from "@/components/OrderItems";
import { StatusBadge, ProcessTimeBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Upload, Image as ImageIcon, History, MessageSquare, CheckCircle2 } from "lucide-react";

const ACTIVE = STATUSES.filter((s) => s !== "Cancelled");

export default function AgentJobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadItem, setUploadItem] = useState("none");

  const load = () => api.get(`/agent/jobs/${id}`).then((r) => setData(r.data)).catch(() => nav("/agent"));
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [id]);

  if (!data) return <div className="text-muted-foreground">Loading...</div>;
  const { job, artworks, history, notes } = data;
  const stepIdx = ACTIVE.indexOf(job.status);
  const paid = (job.deposit_paid || 0) + (job.balance_paid || 0);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let failed = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      if (uploadItem !== "none") fd.append("item_index", uploadItem);
      try { await api.post(`/agent/jobs/${id}/artworks`, fd); } catch { failed++; }
    }
    if (failed) toast.error(`${failed} file(s) failed to upload`); else toast.success(`${files.length} file(s) uploaded`);
    load();
    setUploading(false); e.target.value = "";
  };

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/agent/jobs/${id}/notes`, { text: note });
    setNote("");
    load();
  };

  return (
    <div className="space-y-4" data-testid="agent-job-detail-page">
      <Button variant="ghost" size="sm" onClick={() => nav("/agent")}><ArrowLeft className="w-4 h-4 mr-1" /> My Jobs</Button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-display font-bold font-mono" data-testid="agent-job-number">{job.job_number}</h1>
            <StatusBadge status={job.status} />
            <ProcessTimeBadge job={job} />
          </div>
          <p className="text-sm text-muted-foreground">{job.customer_name} • {job.product} × {job.quantity}</p>
        </div>
        <div className="text-right">
          <div className="text-xs text-muted-foreground">Total</div>
          <div className="font-mono font-bold text-xl">{formatRM(job.total_price)}</div>
          <div className="text-xs text-muted-foreground">Paid {formatRM(paid)}</div>
        </div>
      </div>

      <Card className="p-4" data-testid="agent-progress">
        <div className="text-xs font-mono uppercase text-muted-foreground mb-3">Job Progress (live)</div>
        {job.status === "Cancelled" ? (
          <div className="text-rose-600 font-semibold">Job cancelled</div>
        ) : (
          <div className="flex flex-wrap gap-1">
            {ACTIVE.map((s, i) => (
              <div key={s} className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border ${i < stepIdx ? "bg-emerald-100 border-emerald-300 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300" : i === stepIdx ? "bg-amber-300 border-amber-400 text-slate-900 font-bold" : "text-muted-foreground"}`}>
                {i < stepIdx && <CheckCircle2 className="w-3 h-3" />}{s}
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5 space-y-2 text-sm">
          <h3 className="font-display font-bold">Specifications</h3>
          {job.items?.length > 1 ? <JobItemsList job={job} showPrices /> : <Row k="Product" v={job.product} />}
          {job.variant_name && <Row k="Variant" v={`${job.variant_name}${Object.keys(job.variant_attributes || {}).length ? " — " + Object.entries(job.variant_attributes).map(([k, v]) => `${k}: ${v}`).join(", ") : ""}`} />}
          {Object.entries(job.specs || {}).map(([k, v]) => <Row key={k} k={k} v={v} />)}
                {job.delivery_method && <Row k="Delivery" v={`${job.delivery_method}${job.delivery_fee ? ` (RM ${Number(job.delivery_fee).toFixed(2)})` : ""}`} />}
          {!Object.keys(job.specs || {}).length && <><Row k="Material" v={job.material || "-"} />
          <Row k="Size" v={job.dimensions || "-"} />
          <Row k="Finishing" v={job.finishing || "-"} /></>}
          <Row k="Quantity" v={job.quantity} />
          <Row k="Process Time" v={job.process_time ? `${job.process_time} — ${job.process_turnaround || ""}` : "-"} />
          <Row k="Target Date" v={formatDate(job.deadline)} />
          <Row k="Customer Phone" v={job.customer_phone || "-"} />
          {job.notes && <Row k="Notes" v={job.notes} />}
        </Card>

        <Card className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold">Artwork</h3>
            <div className="flex items-center gap-2">
              {job.items?.length > 1 && (
                <select value={uploadItem} onChange={(e) => setUploadItem(e.target.value)} className="h-8 text-xs border rounded px-2 bg-background" data-testid="agent-upload-item">
                  <option value="none">Whole order</option>
                  {job.items.map((it, i) => <option key={i} value={String(i)}>Item {i + 1}: {it.product}</option>)}
                </select>
              )}
            <label className="cursor-pointer">
              <input type="file" hidden multiple accept="image/*,.pdf,.ai,.psd,.cdr" onChange={upload} data-testid="agent-upload-artwork" />
              <div className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-400 hover:bg-amber-300 text-slate-950 text-sm rounded font-semibold">
                <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload Files"}
              </div>
            </label>
            </div>
          </div>
          {artworks.length === 0 && <div className="text-sm text-muted-foreground text-center py-4">No artwork yet</div>}
          {artworks.map((a) => (
            <div key={a.id} className="border rounded-lg p-3 flex items-start gap-3 text-sm">
              <ImageIcon className="w-6 h-6 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-mono font-semibold truncate">v{a.version} — {a.filename}</div>
                {a.item_product && <div className="text-xs text-amber-700 font-semibold" data-testid={`artwork-item-tag-${a.id}`}>Item {a.item_index + 1}: {a.item_product}</div>}
                <div className="text-xs text-muted-foreground">{formatDateTime(a.created_at)} • {a.uploaded_by}</div>
                <Badge className="mt-1" variant={a.status === "approved" ? "default" : a.status === "revision_requested" ? "destructive" : "secondary"}>{a.status}</Badge>
                {a.file_id && <a href={`${BACKEND_URL}/api/agent/files/${a.file_id}`} target="_blank" rel="noreferrer" className="ml-2 text-xs text-cyan-600 hover:underline">View</a>}
              </div>
            </div>
          ))}
        </Card>

        <Card className="p-5 space-y-3">
          <h3 className="font-display font-bold flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Messages with Rozfa</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {notes.length === 0 && <div className="text-xs text-muted-foreground">No messages yet</div>}
            {notes.map((n) => (
              <div key={n.id} className={`p-2 rounded text-sm ${n.user_name.startsWith("Agent:") ? "bg-amber-50 dark:bg-amber-950/30" : "bg-muted/50"}`}>
                <div className="text-xs text-muted-foreground mb-1">{n.user_name} • {formatDateTime(n.created_at)}</div>
                <div>{n.text}</div>
              </div>
            ))}
          </div>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Write a message to Rozfa..." data-testid="agent-note-input" />
          <Button size="sm" onClick={addNote} className="w-full bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold" data-testid="agent-note-submit">Send Message</Button>
        </Card>

        <Card className="p-5 space-y-2">
          <h3 className="font-display font-bold flex items-center gap-2"><History className="w-4 h-4" /> Timeline</h3>
          {history.map((h) => (
            <div key={h.id} className="flex items-start gap-2 text-sm py-1.5 border-b last:border-0">
              <div className="flex-1">
                <div className="font-medium">{h.action.replace("_", " ")}</div>
                {h.detail && <div className="text-xs text-muted-foreground">{h.detail}</div>}
              </div>
              <div className="text-[10px] text-muted-foreground font-mono shrink-0">{formatDateTime(h.created_at)}</div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between py-1 border-b last:border-0 gap-4">
    <span className="text-muted-foreground shrink-0">{k}</span>
    <span className="text-right">{v}</span>
  </div>
);
